import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { FiArrowRight, FiArrowUpRight, FiBookOpen, FiCheck, FiClock, FiShoppingBag, FiUser } from 'react-icons/fi';
import { usePageSEO } from '../hooks/useSEO';
import { getImageUrl } from '../utils/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useCart } from '../contexts/CartContext';
import { setExpertRefCookie, getPersonaLabel, GOAL_LABELS } from '../utils/expertRef';
import { API_BASE_URL } from '../config';
import ExpertPhotoGallery, { buildExpertGallery } from '../components/expert/ExpertPhotoGallery';
import { expertTheme, focusRing, reducedMotion } from '../theme/expertTheme';

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Page = styled.div`
  --expert-canvas: ${expertTheme.colors.canvas};
  --expert-surface: ${expertTheme.colors.surface};
  --expert-ink: ${expertTheme.colors.ink};
  --expert-muted: ${expertTheme.colors.inkSoft};
  --expert-accent: ${expertTheme.colors.accent};
  --expert-sage: ${expertTheme.colors.sage};
  min-height: 100vh;
  padding-bottom: 112px;
  background:
    radial-gradient(circle at 88% 7%, rgba(105, 125, 84, .12), transparent 27rem),
    var(--expert-canvas);
  color: var(--expert-ink);
`;

const Container = styled.div`
  width: min(${expertTheme.container}, calc(100% - 48px));
  margin: 0 auto;
  @media (max-width: 640px) { width: min(100% - 32px, ${expertTheme.container}); }
`;

const Breadcrumb = styled.nav`
  padding: 24px 0 18px;
  font-size: 13px;
  color: #85877f;
  a { color: ${expertTheme.colors.sage}; font-weight: 600; }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(320px, .84fr) minmax(0, 1.16fr);
  gap: clamp(32px, 5vw, 76px);
  align-items: center;
  padding: clamp(22px, 3vw, 40px);
  border-radius: 36px;
  background: rgba(255, 255, 255, .74);
  border: 1px solid rgba(255,255,255,.9);
  box-shadow: ${expertTheme.shadow.soft};
  backdrop-filter: blur(18px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 20px;
    border-radius: 26px;
  }
`;

const HeroCopy = styled.div`
  padding: 12px 4px 12px 0;
  @media (max-width: 900px) { padding: 4px 4px 12px; }
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px;
  color: ${expertTheme.colors.sage};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
  &::before { content: ''; width: 34px; height: 2px; background: currentColor; }
`;

const ExpertName = styled.h1`
  max-width: 720px;
  margin: 0;
  color: ${expertTheme.colors.ink};
  font-family: var(--font-body);
  font-size: clamp(2.35rem, 5vw, 4.8rem);
  font-weight: 750;
  line-height: .98;
  letter-spacing: -.055em;
`;

const Tagline = styled.p`
  max-width: 650px;
  margin: 24px 0 0;
  color: ${expertTheme.colors.inkSoft};
  font-size: clamp(17px, 1.7vw, 21px);
  line-height: 1.55;
`;

const DEFAULT_HERO_EYEBROW = 'Главный эксперт Lagom Life';
const DEFAULT_ABOUT_TITLE = 'Экспертиза, которой можно доверять';
const DEFAULT_ABOUT_SUBTITLE = 'Научный подход, практический опыт и понятные рекомендации для реальной жизни.';

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 30px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  justify-items: stretch;
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const TrustItem = styled.div`
  min-height: 92px;
  padding: 16px;
  border: 1px solid ${expertTheme.colors.line};
  border-radius: ${expertTheme.radius.small};
  background: rgba(250,248,243,.72);
  color: ${expertTheme.colors.ink};
  font-size: 13px;
  font-weight: 650;
  line-height: 1.4;
  svg { display: block; margin-bottom: 10px; color: ${expertTheme.colors.accent}; }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
`;

const buttonStyles = `
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 13px 22px;
  border-radius: ${expertTheme.radius.pill};
  font-size: 15px;
  font-weight: 700;
  transition: transform 180ms ease, background 180ms ease, color 180ms ease, border-color 180ms ease;
  &:hover { transform: translateY(-2px); }
  &:active { transform: translateY(0); }
  ${focusRing}
  ${reducedMotion}
`;

const PrimaryAction = styled.a`
  ${buttonStyles}
  background: ${expertTheme.colors.accent};
  color: #fff;
  box-shadow: 0 12px 25px rgba(240, 100, 50, .22);
  &:hover { color: #fff; background: ${expertTheme.colors.accentDark}; }
`;

const SecondaryAction = styled(Link)`
  ${buttonStyles}
  color: ${expertTheme.colors.ink};
  border: 1px solid ${expertTheme.colors.line};
  background: rgba(255,255,255,.72);
  &:hover { border-color: ${expertTheme.colors.ink}; }
`;

const AnchorBar = styled.nav`
  position: sticky;
  top: 0;
  z-index: 20;
  margin: 28px 0 0;
  padding: 10px;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  border: 1px solid rgba(222,217,207,.84);
  border-radius: ${expertTheme.radius.pill};
  background: rgba(250,248,243,.9);
  backdrop-filter: blur(16px);
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  a {
    flex: 0 0 auto;
    padding: 9px 15px;
    border-radius: ${expertTheme.radius.pill};
    color: ${expertTheme.colors.inkSoft};
    font-size: 13px;
    font-weight: 700;
    &:hover { color: ${expertTheme.colors.ink}; background: #fff; }
    ${focusRing}
  }
`;

const Section = styled.section`
  scroll-margin-top: 90px;
  padding-top: clamp(68px, 9vw, 116px);
`;

const SectionHeading = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 440px);
  gap: 32px;
  align-items: end;
  margin-bottom: 34px;
  h2 {
    margin: 0;
    color: ${expertTheme.colors.ink};
    font-family: var(--font-body);
    font-size: clamp(2rem, 4.2vw, 4rem);
    font-weight: 760;
    line-height: 1;
    letter-spacing: -.045em;
  }
  p { margin: 0; color: ${expertTheme.colors.inkSoft}; font-size: 16px; line-height: 1.65; }
  @media (max-width: 720px) { grid-template-columns: 1fr; gap: 14px; }
`;

const AboutCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(300px, .85fr);
  gap: clamp(34px, 6vw, 84px);
  padding: clamp(28px, 5vw, 64px);
  border-radius: ${expertTheme.radius.large};
  background: ${expertTheme.colors.charcoal};
  color: #fff;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Prose = styled.div`
  max-width: 740px;
  color: rgba(255,255,255,.82);
  font-size: clamp(16px, 1.7vw, 19px);
  line-height: 1.78;
  p { margin: 0 0 20px; }
  p:last-child { margin-bottom: 0; }
  strong { color: #fff; }
`;

const CredentialsPanel = styled.aside`
  padding: 26px;
  border-radius: ${expertTheme.radius.medium};
  background: #f0eadf;
  color: ${expertTheme.colors.ink};
  h3 { margin: 0 0 20px; font-family: var(--font-body); font-size: 24px; letter-spacing: -.01em; }
`;

const Credential = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 12px;
  padding: 14px 0;
  border-top: 1px solid rgba(32,33,29,.12);
  font-size: 17px;
  line-height: 1.55;
  svg { margin-top: 3px; color: ${expertTheme.colors.sage}; }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
  a { ${buttonStyles} min-height: 44px; color: #fff; border: 1px solid rgba(255,255,255,.22); padding: 10px 16px; }
`;

const CollectionCard = styled.article`
  overflow: hidden;
  border-radius: ${expertTheme.radius.large};
  background: ${expertTheme.colors.surface};
  box-shadow: ${expertTheme.shadow.soft};
  & + & { margin-top: 28px; }
`;

const CollectionHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 32px;
  padding: clamp(26px, 4vw, 46px);
  background: ${expertTheme.colors.sageSoft};
  h3 { margin: 12px 0 10px; color: ${expertTheme.colors.ink}; font-family: var(--font-body); font-size: clamp(1.7rem, 3vw, 2.6rem); letter-spacing: -.035em; }
  p { max-width: 760px; margin: 0; color: ${expertTheme.colors.inkSoft}; line-height: 1.65; }
  @media (max-width: 700px) { grid-template-columns: 1fr; gap: 20px; }
`;

const Badge = styled.span`
  display: inline-flex;
  padding: 7px 12px;
  border-radius: ${expertTheme.radius.pill};
  background: ${expertTheme.colors.sage};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const CollectionTotal = styled.div`
  align-self: end;
  text-align: right;
  span { display: block; color: #6d7168; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; }
  strong { display: block; margin-top: 4px; font-size: 28px; white-space: nowrap; font-variant-numeric: tabular-nums; }
  @media (max-width: 700px) { text-align: left; }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  padding: 24px;
  @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

const ProductCard = styled(Link)`
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  padding: 14px;
  border: 1px solid ${expertTheme.colors.line};
  border-radius: ${expertTheme.radius.medium};
  transition: border-color 180ms ease, transform 180ms ease;
  ${focusRing}
  ${reducedMotion}
  &:hover { border-color: ${expertTheme.colors.sage}; transform: translateY(-2px); }
  img { width: 92px; aspect-ratio: 1; object-fit: cover; border-radius: 14px; background: #f4f1ea; }
  strong { display: block; color: ${expertTheme.colors.ink}; font-size: 14px; line-height: 1.35; }
  span { display: block; margin-top: 8px; color: ${expertTheme.colors.sage}; font-weight: 800; }
`;

const CollectionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 24px;
`;

const CartButton = styled.button`
  ${buttonStyles}
  min-width: 230px;
  background: ${p => p.$added ? expertTheme.colors.sage : expertTheme.colors.accent};
  color: #fff;
  &:hover { background: ${p => p.$added ? expertTheme.colors.sage : expertTheme.colors.accentDark}; }
  &:disabled { cursor: default; transform: none; }
  @media (max-width: 540px) { width: 100%; }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const EditorialCard = styled(Link)`
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: ${expertTheme.radius.medium};
  background: ${expertTheme.colors.surface};
  box-shadow: 0 8px 28px rgba(42,38,30,.06);
  transition: transform 200ms ease, box-shadow 200ms ease;
  ${focusRing}
  ${reducedMotion}
  &:hover { transform: translateY(-5px); box-shadow: ${expertTheme.shadow.hover}; }
`;

const ProgramCard = styled.article`
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: ${expertTheme.radius.medium};
  background: ${expertTheme.colors.surface};
  box-shadow: 0 8px 28px rgba(42,38,30,.06);
  transition: transform 200ms ease, box-shadow 200ms ease;
  ${reducedMotion}
  &:hover { transform: translateY(-5px); box-shadow: ${expertTheme.shadow.hover}; }
`;

const ProgramLink = styled(Link)`
  display: block;
  color: inherit;
  ${focusRing}
`;

const CardImage = styled.div`
  aspect-ratio: 16 / 10;
  background: #ddd7cc center / cover no-repeat;
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 22px;
  h3 { margin: 10px 0 14px; color: ${expertTheme.colors.ink}; font-family: var(--font-body); font-size: 21px; line-height: 1.2; letter-spacing: -.02em; }
  p { margin: 0; color: ${expertTheme.colors.inkSoft}; font-size: 14px; line-height: 1.55; }
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: auto;
  padding-top: 24px;
  color: ${expertTheme.colors.sage};
  font-size: 13px;
  font-weight: 800;
`;

const ProgramBadge = styled(Badge)`
  align-self: flex-start;
  padding: 7px 13px;
  background: #eee8dc;
  color: #514b40;
  border: 1px solid #ddd3c2;
  font-size: 11px;
  letter-spacing: .09em;
`;

const ProgramPrice = styled.span`
  color: ${expertTheme.colors.ink};
  font-size: clamp(22px, 2.1vw, 28px);
  line-height: 1;
  font-weight: 850;
  letter-spacing: -.025em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ProgramMeta = styled(CardMeta)`
  color: ${expertTheme.colors.ink};

  @media (max-width: 540px) {
    align-items: stretch;
    flex-direction: column;

    ${ProgramPrice} {
      font-size: 26px;
    }

    ${CartButton} {
      min-width: 0;
    }
  }
`;

const VideoFrame = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: ${expertTheme.radius.large};
  background: #20211d;
  box-shadow: ${expertTheme.shadow.soft};
  iframe { width: 100%; height: 100%; border: 0; }
`;

const ExpertStrip = styled(Link)`
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 14px;
  border-radius: ${expertTheme.radius.medium};
  background: rgba(255,255,255,.72);
  border: 1px solid ${expertTheme.colors.line};
  ${focusRing}
  img { width: 78px; height: 78px; object-fit: cover; object-position: center center; border-radius: 50%; }
  strong { display: block; font-family: var(--font-body); font-size: 17px; }
  span { color: ${expertTheme.colors.inkSoft}; font-size: 13px; }
`;

const SkeletonWrap = styled.div`
  width: min(${expertTheme.container}, calc(100% - 48px));
  min-height: 620px;
  margin: 0 auto;
  padding: 34px 0;
`;

const Skeleton = styled.div`
  height: 560px;
  border-radius: 36px;
  background: linear-gradient(100deg, #e7e2d8 20%, #f5f1e9 38%, #e7e2d8 56%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  ${reducedMotion}
`;

const ErrorState = styled.div`
  width: min(680px, calc(100% - 32px));
  margin: 80px auto;
  padding: 48px;
  border-radius: ${expertTheme.radius.large};
  background: #fff;
  text-align: center;
  h1 { font-family: var(--font-body); color: ${expertTheme.colors.ink}; }
  p { margin: 0 0 24px; color: ${expertTheme.colors.inkSoft}; }
  a { color: ${expertTheme.colors.accent}; font-weight: 800; }
`;

const MobileAction = styled(Link)`
  display: none;
  @media (max-width: 768px) {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: 82px;
    z-index: 1100;
    min-height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border-radius: ${expertTheme.radius.pill};
    background: ${expertTheme.colors.accent};
    color: #fff;
    font-weight: 800;
    box-shadow: 0 14px 34px rgba(240,100,50,.36);
    ${focusRing}
  }
`;

function normalizeSocialLinks(raw) {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { return []; }
  }
  if (Array.isArray(parsed)) return parsed.filter(item => item?.name && item?.url);
  if (typeof parsed === 'object') return Object.entries(parsed).filter(([, url]) => url).map(([name, url]) => ({ name, url: String(url) }));
  return [];
}

function stripHtml(html) {
  return html ? html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : '';
}

function parseCredentialsItems(html) {
  if (!html) return [];
  const matches = html.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
  if (matches?.length) return matches.map(stripHtml).filter(Boolean);
  return stripHtml(html).split(/[•\n;]|(?:\.\s+(?=[А-ЯA-Z]))/).map(item => item.trim()).filter(item => item.length > 3);
}

function formatPrice(value) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? `${number.toLocaleString('ru-RU')} ₽` : 'Цена по запросу';
}

function toEmbedUrl(url) {
  if (!url) return '';
  if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'www.youtube.com/embed/');
  return url.replace('watch?v=', 'embed/');
}

function buildHeroTrustItems(expert) {
  if (!expert) return [];
  return [expert.hero_trust_1, expert.hero_trust_2, expert.hero_trust_3]
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
}

const ExpertPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [addedKey, setAddedKey] = useState('');
  const { addCollectionToCart, addDigitalToCart } = useCart();

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetch(`${API_BASE_URL}/experts/${slug}`, { credentials: 'include', signal: controller.signal })
      .then(response => response.json())
      .then(result => {
        if (!result.success || !result.data?.expert) throw new Error('not-found');
        setData(result.data);
        setStatus('success');
        if (result.data.expert.referral_code) setExpertRefCookie(result.data.expert.referral_code);
      })
      .catch(error => { if (error.name !== 'AbortError') setStatus('error'); });
    return () => controller.abort();
  }, [slug]);

  const expert = data?.expert;
  const credentials = useMemo(() => parseCredentialsItems(expert?.credentials), [expert?.credentials]);
  const heroTrustItems = useMemo(() => buildHeroTrustItems(expert), [expert]);
  const galleryUrls = useMemo(() => expert ? buildExpertGallery(expert) : [], [expert]);
  const socialLinks = useMemo(() => normalizeSocialLinks(expert?.social_links), [expert?.social_links]);

  usePageSEO({
    title: expert?.meta_title || `${expert?.display_name || 'Эксперт'} — Lagom Life`,
    description: expert?.meta_description || expert?.tagline || stripHtml(expert?.bio),
    jsonLd: expert ? {
      '@context': 'https://schema.org', '@type': 'Person', name: expert.display_name,
      description: expert.tagline || stripHtml(expert.bio), image: galleryUrls[0] || undefined,
      url: `${window.location.origin}/expert/${expert.slug}`
    } : null
  });

  if (status === 'loading') return <SkeletonWrap aria-label="Загрузка профиля"><Skeleton /></SkeletonWrap>;
  if (status === 'error' || !expert) return <ErrorState><h1>Профиль не найден</h1><p>Возможно, ссылка устарела или страница ещё не опубликована.</p><Link to="/experts">Вернуться к экспертам</Link></ErrorState>;

  const programs = data.digitalProducts || [];
  const collections = data.collections || [];
  const articles = data.articles || [];
  const otherExperts = data.otherExperts || [];
  const personaLabel = getPersonaLabel(expert.persona_type);
  const heroEyebrow = expert.hero_eyebrow?.trim() || DEFAULT_HERO_EYEBROW;
  const aboutTitle = expert.about_section_title?.trim() || DEFAULT_ABOUT_TITLE;
  const aboutSubtitle = expert.about_section_subtitle?.trim() || DEFAULT_ABOUT_SUBTITLE;
  const primaryAnchor = collections.length ? '#collections' : programs.length ? '#programs' : '#about';

  const markAdded = (key) => {
    setAddedKey(key);
    window.setTimeout(() => setAddedKey(current => current === key ? '' : current), 2200);
  };

  return (
    <Page>
      <Container>
        <Breadcrumb aria-label="Хлебные крошки"><Link to="/experts">Эксперты</Link> / {expert.display_name}</Breadcrumb>
        <Hero>
          <ExpertPhotoGallery expert={expert} alt={expert.display_name} />
          <HeroCopy>
            <Eyebrow>
              {heroEyebrow}
              {personaLabel && personaLabel !== 'Эксперт' ? ` · ${personaLabel}` : ''}
            </Eyebrow>
            <ExpertName>{expert.display_name}</ExpertName>
            {expert.tagline ? <Tagline>{expert.tagline}</Tagline> : null}
            {heroTrustItems.length ? (
              <TrustGrid>
                {heroTrustItems.map((item) => (
                  <TrustItem key={item}><FiCheck size={18} aria-hidden="true" />{item}</TrustItem>
                ))}
              </TrustGrid>
            ) : null}
            <Actions>
              <PrimaryAction href={primaryAnchor}>Смотреть материалы <FiArrowRight aria-hidden="true" /></PrimaryAction>
              <SecondaryAction to="/user/registration">Присоединиться</SecondaryAction>
            </Actions>
          </HeroCopy>
        </Hero>

        <AnchorBar aria-label="Разделы страницы">
          {expert.bio?.trim() ? <a href="#about">Об эксперте</a> : null}
          {collections.length ? <a href="#collections">Рекомендации</a> : null}
          {programs.length ? <a href="#programs">Программы</a> : null}
          {articles.length ? <a href="#articles">Статьи</a> : null}
          {expert.video_url ? <a href="#video">Видео</a> : null}
        </AnchorBar>

        {expert.bio?.trim() ? (
          <Section id="about">
            <SectionHeading><h2>{aboutTitle}</h2><p>{aboutSubtitle}</p></SectionHeading>
            <AboutCard>
              <div>
                <Prose dangerouslySetInnerHTML={{ __html: sanitizeHtml(expert.bio) }} />
                {socialLinks.length ? <SocialLinks>{socialLinks.map(item => <a key={`${item.name}-${item.url}`} href={item.url} target="_blank" rel="noreferrer">{item.name}<FiArrowUpRight /></a>)}</SocialLinks> : null}
              </div>
              {credentials.length ? <CredentialsPanel><h3>Регалии и достижения</h3>{credentials.map(item => <Credential key={item}><FiCheck size={18} aria-hidden="true" /><span>{item}</span></Credential>)}</CredentialsPanel> : null}
            </AboutCard>
          </Section>
        ) : null}

        {collections.length ? (
          <Section id="collections">
            <SectionHeading><h2>Рекомендации эксперта</h2><p>Готовые сочетания продуктов, собранные вокруг конкретной цели и удобные для заказа одним действием.</p></SectionHeading>
            {collections.map(collection => {
              const products = collection.products || [];
              const total = products.reduce((sum, product) => sum + (Number.parseFloat(product.price) || 0), 0);
              const key = `collection-${collection.id}`;
              return (
                <CollectionCard key={collection.id}>
                  <CollectionHeader>
                    <div><Badge>{GOAL_LABELS[collection.goal] || collection.goal}</Badge><h3>{collection.title}</h3>{collection.description ? <p>{collection.description}</p> : null}</div>
                    <CollectionTotal><span>Стоимость набора</span><strong>{formatPrice(total)}</strong></CollectionTotal>
                  </CollectionHeader>
                  <ProductGrid>
                    {products.map(product => <ProductCard key={product.id} to={`/product/${product.id}`}><img src={getImageUrl(product.featured_image)} alt="" loading="lazy" /><div><strong>{product.name}</strong><span>{formatPrice(product.price)}</span></div></ProductCard>)}
                  </ProductGrid>
                  <CollectionFooter><CartButton type="button" $added={addedKey === key} disabled={addedKey === key} onClick={() => { addCollectionToCart(products); markAdded(key); }}>{addedKey === key ? <><FiCheck /> Добавлено</> : <><FiShoppingBag /> Добавить подборку</>}</CartButton></CollectionFooter>
                </CollectionCard>
              );
            })}
          </Section>
        ) : null}

        {programs.length ? (
          <Section id="programs">
            <SectionHeading><h2>Цифровые программы</h2><p>Пошаговые материалы эксперта, которые можно проходить в удобном темпе.</p></SectionHeading>
            <CardGrid>{programs.map(product => {
              const key = `digital-${product.id}`;
              return <ProgramCard key={product.id}><ProgramLink to={`/expert/${slug}/program/${product.slug}`}><CardImage style={{ backgroundImage: `url(${getImageUrl(product.cover_image)})` }} /></ProgramLink><CardBody><ProgramBadge>Программа</ProgramBadge><ProgramLink to={`/expert/${slug}/program/${product.slug}`}><h3>{product.title}</h3></ProgramLink>{product.duration_label ? <p><FiClock /> {product.duration_label}</p> : null}<ProgramMeta><ProgramPrice>{formatPrice(product.price)}</ProgramPrice><CartButton type="button" $added={addedKey === key} disabled={addedKey === key} onClick={() => { addDigitalToCart(product); markAdded(key); }}>{addedKey === key ? 'Добавлено' : 'В корзину'}</CartButton></ProgramMeta></CardBody></ProgramCard>;
            })}</CardGrid>
          </Section>
        ) : null}

        {articles.length ? (
          <Section id="articles">
            <SectionHeading><h2>Материалы эксперта</h2><p>Разборы и практические статьи, в которых сложное становится понятным.</p></SectionHeading>
            <CardGrid>{articles.map(article => <EditorialCard key={article.id} to={`/news/${article.slug}`}><CardImage style={{ backgroundImage: `url(${getImageUrl(article.main_image || article.image_url || article.image)})` }} /><CardBody><Badge>Статья</Badge><h3>{article.title}</h3>{article.excerpt ? <p>{stripHtml(article.excerpt).slice(0, 150)}</p> : null}<CardMeta><span><FiBookOpen /> Читать</span><FiArrowUpRight size={20} /></CardMeta></CardBody></EditorialCard>)}</CardGrid>
          </Section>
        ) : null}

        {expert.video_url ? <Section id="video"><SectionHeading><h2>Видео с экспертом</h2><p>Познакомьтесь с подходом и рекомендациями в живом формате.</p></SectionHeading><VideoFrame><iframe title={`Видео: ${expert.display_name}`} src={toEmbedUrl(expert.video_url)} allowFullScreen loading="lazy" /></VideoFrame></Section> : null}

        {otherExperts.length ? <Section><SectionHeading><h2>Другие эксперты</h2><p>Специалисты Lagom Life с разными направлениями практики.</p></SectionHeading><CardGrid>{otherExperts.map(item => <ExpertStrip key={item.slug} to={`/expert/${item.slug}`}><img src={getImageUrl(item.photoUrl)} alt="" loading="lazy" /><div><strong>{item.displayName}</strong><span>Открыть профиль</span></div><FiArrowUpRight /></ExpertStrip>)}</CardGrid></Section> : null}
      </Container>
      <MobileAction to="/user/registration"><FiUser /> Присоединиться</MobileAction>
    </Page>
  );
};

export default ExpertPage;
